import { useState } from 'react'
import { ChevronDownIcon } from './ui'
import { OFF_ROAD_RECOVERY_OPTIONS } from './offRoadRecoveryOptions'

function RadioIconSmall({ checked }: { checked: boolean }) {
  return (
    <div
      className={`relative shrink-0 size-[16px] rounded-full border border-solid ${
        checked ? 'bg-background-brand-solid border-background-brand-solid' : 'border-border-primary'
      }`}
    >
      {checked && <div className="absolute inset-[31.25%] bg-background-primary rounded-full" />}
    </div>
  )
}

export type OffRoadRecoveryAccordionProps = {
  id: string
  value: string
  onChange: (next: string) => void
  /** Value from loaded policy (mapped option value), e.g. "1500" */
  baselineValue: string | null
}

export function isOffRoadRecoveryUnsaved(value: string, baselineValue: string | null): boolean {
  const v = (value || '1500').trim()
  const b = baselineValue == null || baselineValue === '' ? '1500' : baselineValue.trim()
  return v !== b
}

/**
 * Off-road recovery tier chooser — same interaction pattern as {@link OvernightParkingAccordion}:
 * collapsed trigger + expandable list, “On your policy now” on the baseline row, yellow trigger border when unsaved.
 */
export default function OffRoadRecoveryAccordion({
  id,
  value,
  onChange,
  baselineValue,
}: OffRoadRecoveryAccordionProps) {
  const [expanded, setExpanded] = useState(false)
  const selected = OFF_ROAD_RECOVERY_OPTIONS.find((o) => o.value === value) ?? OFF_ROAD_RECOVERY_OPTIONS[0]
  const hasSelection = Boolean(value)
  const unsaved = isOffRoadRecoveryUnsaved(value, baselineValue)

  const triggerBorderClass = unsaved
    ? 'border border-yellow-500'
    : hasSelection
      ? 'border-2 border-brand-border'
      : 'border-2 border-border-primary'

  const triggerFocusRingClass = unsaved
    ? 'focus-visible:ring-2 focus-visible:ring-yellow-500/80 focus-visible:ring-offset-0'
    : 'focus-visible:ring-2 focus-visible:ring-brand-border focus-visible:ring-offset-0'

  return (
    <div className="flex flex-col gap-0 w-full">
      <button
        id={id}
        type="button"
        aria-expanded={expanded}
        aria-controls={`${id}-panel`}
        onClick={() => setExpanded((o) => !o)}
        className={`bg-background-primary flex gap-[12px] items-start justify-between px-[12px] py-[10px] rounded-lg w-full text-left transition-colors focus:outline-none ${triggerFocusRingClass} ${triggerBorderClass}`}
      >
        <div className="flex flex-1 flex-col gap-[2px] items-start min-w-0 pr-[8px]">
          <p className="font-body font-semibold text-md leading-md text-text-primary-900">
            {selected.label}
            {baselineValue != null && baselineValue !== '' && selected.value === baselineValue && (
              <span className="font-body font-semibold text-sm leading-sm text-brand-solid ml-[6px]">
                — On your policy now
              </span>
            )}
          </p>
          <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">{selected.description}</p>
        </div>
        <div className="flex items-center shrink-0 self-start pt-[2px]">
          <span
            className={`text-text-tertiary-600 transition-transform duration-[250ms] ease-in-out motion-reduce:transition-none ${
              expanded ? 'rotate-180' : 'rotate-0'
            }`}
            aria-hidden
          >
            <ChevronDownIcon className="w-4 h-4" />
          </span>
        </div>
      </button>

      <div
        id={`${id}-panel`}
        className={`grid w-full transition-[grid-template-rows] duration-[250ms] ease-in-out motion-reduce:transition-none ${
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          {expanded && (
            <div className="flex flex-col gap-[12px] items-start pt-[12px] w-full">
              {OFF_ROAD_RECOVERY_OPTIONS.filter((o) => o.value !== value).map((option) => {
                const isOnPolicy = baselineValue != null && baselineValue !== '' && option.value === baselineValue
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value)
                      setExpanded(false)
                    }}
                    className={`border-2 flex gap-[12px] items-start justify-between p-[16px] relative rounded-xl shrink-0 w-full text-left transition-colors focus:outline-none focus:ring-2 focus:ring-brand-border ${
                      isOnPolicy
                        ? 'bg-background-tertiary border-brand-border shadow-xs'
                        : 'bg-background-primary border-border-secondary'
                    }`}
                  >
                    <div className="flex flex-1 flex-col gap-[2px] items-start min-w-0 pr-[8px]">
                      <p className="font-body font-semibold text-md leading-md text-text-primary-900">
                        {option.label}
                        {isOnPolicy && (
                          <span className="font-body font-semibold text-sm leading-sm text-brand-solid ml-[6px]">
                            — On your policy now
                          </span>
                        )}
                      </p>
                      <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
                        {option.description}
                      </p>
                    </div>
                    <div className="flex items-start justify-center shrink-0 pt-[2px]">
                      <RadioIconSmall checked={false} />
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
        </div>
    </div>
  )
}
